import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  Link,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useGA4React } from 'ga-4-react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { CountryList, SourceOfFundsList, SourceOfWealthList } from '../../libs/staticData';
import PrimaryButton from '../ui/buttons/primaryButton';
import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';

import { updateFund } from '../../features/data/data.slice';

import { objectToArray } from '../../libs/helpers';
import SearchDropdown from '../ui/forms/fields/searchDropDoown';
import { Report } from '../../libs/loggers';

const ReInvestRow = (props) => {
  const { fund } = props;
  const dispatch = useDispatch();

  const funds = useSelector((state) => state.data.value.funds);
  const submition = useSelector((state) => state.submition.data);

  const validationSchema = yup.object().shape({
    reInvest: yup.string().oneOf(['Yes', 'No']).required('Please select one.'),
  });

  const reinvestFund = submition?.reinvestFunds?.find((item) => item.id === fund.fund.id);

  const {
    control,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
  });

  const reInvestWatch = watch('reInvest');

  useEffect(() => {
    if (reInvestWatch && reInvestWatch !== fund.reInvest) {
      const savedFund = funds.find((x) => x.fund.id === fund.fund.id);

      if (!savedFund) {
        console.warn('ReInvestRow - savedFund not found for fund:', fund.fund.id);
        return;
      }

      dispatch(
        updateFund({
          id: savedFund.fund.id,
          data: { reInvest: reInvestWatch },
        })
      );

      props.onChange({
        id: savedFund.fund.id,
        reInvest: reInvestWatch,
      });

      trigger('reInvest');
    } else if (!reInvestWatch) {
      console.log('ReInvestRow - no reInvest selected, triggering validation');
      trigger('reInvest');
    }
  }, [reInvestWatch, dispatch, funds, fund, trigger, props]);

  return (
    <Grid container spacing={3}>
      <Grid item>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ mt: 1 }}
        >
          <Typography>{fund.fund.name}</Typography>
        </Box>
      </Grid>

      <Grid item>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
          <Select
            name="reInvest"
            label="Re-invest distributions?"
            defaultValue={reinvestFund?.reInvest || fund.reInvest || ''}
            control={control}
            error={!!errors.reInvest}
            helperText={errors.reInvest && errors.reInvest.message}
            sx={{ width: '180px' }}
            required
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
        </Box>
      </Grid>
    </Grid>
  );
};

function InvestmentDetails({
  extendedForm,
  data,
  submitionData,
  onNext,
  onBack,
  stepSlug,
  openSave,
  onSaveAndExit,
  isRaHelp,
  isBciApplication,
}) {
  let ga4React = useGA4React();
  const loading = useSelector((state) => state.submition.submition?.loading);
  const userId = useSelector((state) => state.submition.data?.userId);
  const page = window.location.pathname.replace(/^\/|\/$/g, '');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const submitionId = useSelector((state) => state.submition.submition?.submitionId);
  const formStepper = useSelector((state) => state.formStepper.value);

  const InvestmentDetailsFormValidationSchema = (data, extendedForm, isRaHelp) => {
    const validationSchema = yup.object().shape({
      stepSlug: yup.string().required(),
      reinvestFunds: yup
        .array()
        .test(
          'reinvestSelected',
          'Please complete your distribution re-investment preference.',
          function (val) {
            let isValid = true;

            if (val) {
              for (let i = 0; i < val.length; i++) {
                const item = val[i];
                if (!item.reInvest || item.reInvest === '') {
                  return false;
                }
              }
            }
            return isValid;
          }
        ),
      sourceOfFunds: yup.string().required('Please select the source of your funds.'),
      sourceOfFundsOther: yup.string().when('sourceOfFunds', {
        is: (sourceOfFunds) => sourceOfFunds === 'other',
        then: yup.string().required("Please specify 'other' source of funds."),
        otherwise: yup.string(),
      }),
      purposeOfInvestment: yup.string().required('Please complete this field.'),
      sourceOfWealth: yup.string().required('Please select the source of your wealth.'),
      sourceOfWealthOther: yup.string().when('sourceOfWealth', {
        is: (sourceOfWealth) => sourceOfWealth === 'other',
        then: yup.string().required("Please specify 'other' source of wealth."),
        otherwise: yup.string(),
      }),

      ...(isRaHelp
        ? {
            sof_countryOrigin_funds: yup.string().required('Please complete Country of Funds.'),
            otherwise: yup.string(),
          }
        : {}),
    });
    return validationSchema;
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(InvestmentDetailsFormValidationSchema(data, extendedForm, isRaHelp)),
  });

  const selectedSourceOfWealth = watch('sourceOfWealth');
  const selectedSourceOfFunds = watch('sourceOfFunds');

  useEffect(() => {
    setValue('stepSlug', stepSlug, { shouldValidate: true });
  }, [stepSlug, setValue]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (submitionData) {
      Object.entries(submitionData).forEach(([key, value]) => {
        setValue(key, value, { validate: true });
      });
    }
  }, [submitionData, setValue]);

  useEffect(() => {
    if (selectedSourceOfWealth !== 'other') {
      setValue('sourceOfWealthOther', '');
    }
    if (selectedSourceOfFunds !== 'other') {
      setValue('sourceOfFundsOther', '');
    }
  }, [selectedSourceOfWealth, selectedSourceOfFunds, setValue]);

  useEffect(() => {
    register('reinvestFunds');
    if (data.funds) {
      let initialValues = [];
      for (let i = 0; i < data.funds.length; i++) {
        let fund = data.funds[i];
        initialValues.push({
          id: fund.fund.id,
          reInvest: fund.reInvest ? fund.reInvest : null,
        });
      }

      setValue('reinvestFunds', initialValues, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [data, register, setValue]);

  useEffect(() => {
    const eventInfo = {
      action: 'view',
      category: 'step4_formstep_view',
      description: 'viewed a form step in step 4',
      value: {
        currentPage: page,
        stepSlug,
        activeFormStep: formStepper?.step,
      },
    };

    Report(eventInfo, userId, submitionId);
  }, []);

  const handleReInvestUpdated = (data) => {
    if (data) {
      let currentValues = getValues('reinvestFunds');
      if (currentValues) {
        const index = currentValues.findIndex((a) => a.id === data.id);

        if (index !== -1) {
          currentValues[index].reInvest = data.reInvest;
        }

        setValue('reinvestFunds', currentValues, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  };

  const NavButtons = (props) => (
    <>
      <Box display="flex" justifyContent={'flex-end'}>
        <PrimaryButton disabled={props.isFirstStep} onClick={() => onBack()} sx={{ mt: 1, mr: 1 }}>
          Back
        </PrimaryButton>
        <PrimaryButton
          onClick={() => handleNext()}
          disabled={loading}
          sx={{
            mt: 1,
            mr: 1,
            '&.Mui-disabled': {
              background: '#CC6323',
              color: '#fff',
            },
          }}
        >
          {userId ? 'Save & Continue' : 'Continue'}{' '}
          {loading && <CircularProgress sx={{ ml: 1 }} size={isMobile ? 15 : 20} color="inherit" />}
        </PrimaryButton>
        <PrimaryButton variant="outlined" onClick={() => handleSaveAndExit(userId)} sx={{ mt: 1 }}>
          {'Save & Exit'}
        </PrimaryButton>
      </Box>
      {!userId && (
        <Box display="flex" justifyContent={'flex-end'} marginTop={1}>
          <Typography variant="body1">
            {' '}
            Your progress is not being saved. Click{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              variant="body1"
              underline="always"
              onClick={() => {
                handleOpenSave();
              }}
            >
              here
            </Link>{' '}
            to save.
          </Typography>
        </Box>
      )}
    </>
  );

  const handleNext = async () => {
    ga4React.event('complete_form_step', `${stepSlug}`, 'investement_submition');

    setValue('completeProcess', false, { shouldValidate: true });

    handleSubmit(onSubmit)();
  };

  const handleSaveAndExit = (userId) => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };

    console.log('data   ========>', data);
    onSaveAndExit(newSubmition, userId);
  };

  const handleOpenSave = () => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    openSave(newSubmition);
  };

  const onSubmit = (data) => {
    const newSubmition = { ...submitionData, ...data };
    onNext(newSubmition);
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          <Typography variant="h5" color="primary">
            Investment Distributions
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            leaveTouchDelay={2500}
            enterTouchDelay={0}
            title={
              <Typography>
                Some investments receive income distributions. If you select 'No', your
                distributions will be paid out into the bank account specified. If your distribution
                is below R1,000 or you select 'Yes', your distribution will automatically be
                re-invested.
              </Typography>
            }
          >
            <Button startIcon={<InfoIcon color="primary" size={'small'} />}>
              <FormHelperText sx={{ textTransform: 'capitalize' }}> Tell me more.</FormHelperText>
            </Button>
          </Tooltip>
          {data.funds &&
            data.funds.map((fund, index) => (
              <Box key={index} display={'flex'} sx={{ mt: 2 }}>
                <ReInvestRow
                  key={fund.fund.id}
                  fund={fund}
                  onChange={(data) => handleReInvestUpdated(data)}
                />
              </Box>
            ))}
          <ErrorMessage name="reinvestFunds" errors={errors} as={<Typography color="error" />} />
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ mt: 4, mb: 4 }}>
        <Grid item xs={12} md={12}>
          <Typography variant="h5" color="primary">
            Source of Funds
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Select
            name="sourceOfFunds"
            label="Source of Funds"
            control={control}
            error={!!errors.sourceOfFunds}
            helperText={errors?.sourceOfFunds?.message}
            required
          >
            {SourceOfFundsList.map((item) => {
              if (item.rules && item.rules.vehicle === data.vehicle) {
                return (
                  <MenuItem key={item.key} value={item.key}>
                    {item.value}
                  </MenuItem>
                );
              } else if (!item.rules) {
                return (
                  <MenuItem key={item.key} value={item.key}>
                    {item.value}
                  </MenuItem>
                );
              } else {
                return null;
              }
            })}
            <MenuItem value="other">Other</MenuItem>
          </Select>

          <Tooltip
            leaveTouchDelay={2500}
            enterTouchDelay={0}
            title={
              <Typography>Please indicate the source of funds for this transaction</Typography>
            }
          >
            <Button startIcon={<InfoIcon color="primary" size={'small'} />}>
              <FormHelperText sx={{ textTransform: 'capitalize' }}> Tell me more.</FormHelperText>
            </Button>
          </Tooltip>
          {watch('sourceOfFunds') === 'other' ? (
            <Input
              label="Please specify Other source"
              {...register('sourceOfFundsOther')}
              error={!!errors.sourceOfFundsOther}
              helperText={errors?.sourceOfFundsOther?.message}
              required
            />
          ) : null}
          {isRaHelp && (
            <SearchDropdown
              name="sof_countryOrigin_funds"
              label="Origin country your of Source of Funds"
              control={control}
              error={!!errors.sof_countryOrigin_funds}
              helperText={errors?.sof_countryOrigin_funds?.message}
              options={CountryList}
              required
            />
          )}

          <Input
            label="Purpose of Investments"
            {...register('purposeOfInvestment')}
            error={!!errors.purposeOfInvestment}
            required
            helperText={
              !errors.purposeOfInvestment
                ? 'Purpose of Investments (example: long term savings, saving toward retirement, education, short terms savings, etc.)'
                : errors?.purposeOfInvestment?.message
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Select
            name="sourceOfWealth"
            label="Source of Wealth"
            control={control}
            required
            error={!!errors.sourceOfWealth}
            helperText={errors?.sourceOfWealth?.message}
          >
            {SourceOfWealthList.map((item) => (
              <MenuItem key={item.key} value={item.key}>
                {item.value}
              </MenuItem>
            ))}
            <MenuItem value="other">Other</MenuItem>
          </Select>

          <Tooltip
            leaveTouchDelay={2500}
            enterTouchDelay={0}
            title={
              <Typography>
                Please indicate the activity that has most largely contributed to your total net
                worth
              </Typography>
            }
          >
            <Button startIcon={<InfoIcon color="primary" size={'small'} />}>
              <FormHelperText sx={{ textTransform: 'capitalize' }}> Tell me more.</FormHelperText>
            </Button>
          </Tooltip>

          {watch('sourceOfWealth') === 'other' ? (
            <Input
              label="Please specify Other source"
              {...register('sourceOfWealthOther')}
              error={!!errors.sourceOfWealthOther}
            />
          ) : null}
        </Grid>
        <Grid item xs={12} md={12}>
          <NavButtons isLastStep={false} isFirstStep={false} index={0} />
        </Grid>
        <Box display={'inlineBlock'}>
          {objectToArray(errors).map((error, index) => (
            <Typography color={'error'} key={index}>
              {error.value.message}
            </Typography>
          ))}
        </Box>
      </Grid>
    </>
  );
}

export default InvestmentDetails;
